import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MovieDetailModal from '../components/MovieDetailModal';
import {
  findExistingRankedPost,
  findViewerPostForMovie,
  findWatchlistEntryForMovie,
  hasMovieInWatchlist,
} from '../utils/rankingBins';

export default function AddFilmFlow({
  posts,
  watchlist = [],
  onBeginRanking,
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialStep, setModalInitialStep] = useState('detail');
  const [rankLaunchContext, setRankLaunchContext] = useState(null);
  const apiBaseUrl = useMemo(
    () => process.env.REACT_APP_API_BASE_URL || '',
    []
  );
  const [results, setResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchError, setSearchError] = useState(null);
  const query = searchText.trim();

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (query.length < 2) {
        setResults([]);
        setSearchStatus('idle');
        setSearchError(null);
        return;
      }
      if (selectedMovie) {
        return;
      }

      // Enter loading state immediately so empty-state text does not flash
      // during the debounce window for a fresh query.
      setSearchStatus('loading');
      setSearchError(null);
      try {
        const res = await fetch(
          `${apiBaseUrl}/api/movies/search?q=${encodeURIComponent(query)}`
        );
        const body = await res.json();
        if (cancelled) return;
        if (!body.ok) {
          setSearchStatus('error');
          setSearchError(body.error || 'Search failed');
          return;
        }
        setResults(body.results || []);
        setSearchStatus('idle');
      } catch (err) {
        if (cancelled) return;
        setSearchStatus('error');
        setSearchError(String(err));
      }
    }

    if (!selectedMovie && query.length >= 2) {
      // Mark as loading before debounce delay begins.
      setSearchStatus('loading');
      setSearchError(null);
    }
    const t = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [apiBaseUrl, query, selectedMovie]);

  useEffect(() => {
    const m = location.state?.openMovieModal;
    if (!m || !String(m.title || '').trim()) return;
    const returnAfterRank = location.state?.returnAfterRank;
    const removeWatchlistEntryId = location.state?.rankFromWatchlistEntryId;
    setRankLaunchContext({
      returnAfterRank:
        typeof returnAfterRank === 'string' && returnAfterRank.startsWith('/')
          ? returnAfterRank
          : null,
      removeWatchlistEntryId:
        removeWatchlistEntryId != null ? removeWatchlistEntryId : null,
    });
    setModalInitialStep('detail');
    setSelectedMovie(m);
    setModalOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const openMovieModal = (movie) => {
    setRankLaunchContext(null);
    setModalInitialStep('detail');
    setSelectedMovie(movie);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMovie(null);
    setModalInitialStep('detail');
    setRankLaunchContext(null);
  };

  const beginRanking = (watchedMovie) => {
    const removeWatchlistEntryId = rankLaunchContext?.removeWatchlistEntryId ?? null;
    onBeginRanking({
      ...watchedMovie,
      ...(rankLaunchContext?.returnAfterRank
        ? { afterRankNavigate: rankLaunchContext.returnAfterRank }
        : {}),
      ...(removeWatchlistEntryId != null
        ? { removeWatchlistEntryId }
        : {}),
      onBackToTierSelection: () => {
        setSelectedMovie(watchedMovie?.selectedMovie || null);
        setModalInitialStep('pickBin');
        setModalOpen(true);
        setRankLaunchContext({
          returnAfterRank: rankLaunchContext?.returnAfterRank || null,
          removeWatchlistEntryId,
        });
      },
    });
  };

  const existingForModal = selectedMovie
    ? findExistingRankedPost(posts, selectedMovie)
    : null;

  const modalViewerHasWatched = Boolean(
    selectedMovie && findViewerPostForMovie(posts, selectedMovie),
  );
  const modalInWatchlist = selectedMovie
    ? hasMovieInWatchlist(watchlist, selectedMovie)
    : false;
  const modalWatchlistEntry = selectedMovie
    ? findWatchlistEntryForMovie(watchlist, selectedMovie)
    : null;
  const hasSearchText = query.length > 0;
  const isShortQuery = hasSearchText && query.length < 2;
  const showResults = !selectedMovie && results.length > 0;
  const showEmptyState =
    !selectedMovie &&
    query.length >= 2 &&
    searchStatus === 'idle' &&
    results.length === 0 &&
    !searchError;
  const showInitialLoading = !selectedMovie && searchStatus === 'loading' && results.length === 0;
  const showRefreshing = !selectedMovie && searchStatus === 'loading' && results.length > 0;
  const showError = !selectedMovie && searchStatus === 'error';

  return (
    <section className="flique-add-landing">
      <header className="flique-add-hero text-center mb-3">
        <h1 className="flique-add-hero-title">Flique It!</h1>
        <p className="flique-add-hero-tagline px-2 mb-0 mx-auto">
          Search for a film, log it, and rank it against what you&apos;ve already
          seen.
        </p>
      </header>

      <div className="flique-add-hero-search mb-3">
        <label className="form-label visually-hidden" htmlFor="flique-movie-search">
          Search movies
        </label>
        <div className="flique-add-search-input-shell">
          <input
            id="flique-movie-search"
            type="search"
            className="form-control form-control-lg flique-add-search-input"
            placeholder="Search titles…"
            autoComplete="off"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setSelectedMovie(null);
              setModalOpen(false);
            }}
          />
          {searchText.trim() ? (
            <button
              type="button"
              className="flique-icon-remove-x flique-add-search-clear"
              aria-label="Clear movie search"
              onClick={() => {
                setSearchText('');
                setSelectedMovie(null);
                setModalOpen(false);
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {showResults && (
        <div className="flique-search-results mb-3">
          {results.map((movie) => {
            const alreadyWatched = Boolean(findViewerPostForMovie(posts, movie));
            const alreadyInWatchlist = hasMovieInWatchlist(watchlist, movie);
            return (
              <button
                key={movie.tmdb_id}
                type="button"
                className="flique-search-result"
                onClick={() => openMovieModal(movie)}
              >
                <div className="d-flex gap-3 align-items-center w-100">
                  {movie.poster_url ? (
                    <img
                      src={movie.poster_url}
                      alt=""
                      className="flique-search-poster"
                    />
                  ) : (
                    <div className="flique-search-poster flique-search-poster-empty">
                      —
                    </div>
                  )}
                  <div className="text-start flex-grow-1">
                    <div className="fw-semibold">
                      {movie.title}
                      {movie.year ? ` (${movie.year})` : ''}
                    </div>
                    {movie.director ? (
                      <small className="text-muted">{movie.director}</small>
                    ) : null}
                  </div>
                  {alreadyWatched ? (
                    <span
                      className="flique-feed-engage-watched-badge flique-search-watched-badge"
                      role="status"
                      aria-label="You watched this movie"
                    >
                      <span
                        className="flique-feed-engage-watched-icon"
                        aria-hidden="true"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      <span className="flique-feed-engage-watched-text">Watched</span>
                    </span>
                  ) : alreadyInWatchlist ? (
                    <span className="flique-search-status-chip">
                      Watchlist
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!selectedMovie && isShortQuery && (
        <p className="small text-muted text-center">
          Type at least 2 characters to search.
        </p>
      )}

      {showRefreshing && (
        <p className="small text-muted text-center">Refreshing results…</p>
      )}

      {showInitialLoading && (
        <p className="small text-muted text-center">Searching…</p>
      )}

      {showEmptyState && (
        <p className="small text-muted text-center mb-3">
          No movies found for &quot;{query}&quot;.
        </p>
      )}

      {showError && (
        <div className="text-danger small mb-3 text-center">
          Couldn&apos;t refresh search results right now. {searchError}
        </div>
      )}

      <MovieDetailModal
        movie={selectedMovie}
        existingPost={existingForModal}
        open={modalOpen}
        initialStep={modalInitialStep}
        onClose={closeModal}
        onBeginRanking={beginRanking}
        onAddToWatchMovieList={onAddToWatchMovieList}
        onRemoveFromWatchlist={onRemoveFromWatchlist}
        viewerHasWatchedMovie={modalViewerHasWatched}
        inWatchlist={modalInWatchlist}
        watchlistEntryId={modalWatchlistEntry?.id}
      />
    </section>
  );
}
